import { Component, computed, inject, Signal } from '@angular/core';
import { Sort } from '../../shared/components/table/table/table.component';
import { Observable } from 'rxjs';
import { maxDeelVanJaar, maxOverMapped, maxSumOfStacksIf, noAgg0, sumOver } from '../../services/aggregation';
import { Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	DoorstroomMeasure,
	ExportDataOptions,
	xAggExcept,
} from '../../services/data.service';
import { att, att0, count_records, percOfTotal } from '../../services/measures';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { defaultDoorstroomActueelFilters, defaultDoorstroomHistorieFilters } from '../../services/filter-config';
import { range } from 'lodash-es';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, BaseDashboardConfig, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { Axis, createYAxis } from '../../services/axis';
import { DashboardVariant, Eenheid } from '../../services/weergave-opties';
import { PsName } from '../../services/page-state.service';
import { BarInfo } from '../../services/stacked-bars';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { formatPercent } from '@angular/common';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface UitstroomI extends Attributes {
	ds_m_nr_weging: number;
	ds_is_prognose: string;
	xa: { [nr: number]: { ds_m_nr_weging: number } };
	ds_m_nr_leerlingen: number;
}

interface UitstroomA extends Attributes {
	max: number;
	ds_m_nr_weging: number;
	ds_is_prognose: number;
	maxPctJaar: number;
	ds_m_nr_leerlingen: number;
}

enum MeasureColumn {
	LEERLINGEN = 'Leerlingen',
	PERCENTAGE = 'Percentage',
}

const historieSchooljaarGroup: AttrPath = ['ds_nm_schooljaar_van'];

@Component({
	selector: 'app-instroom',
	templateUrl: './uitstroom.component.html',
	styleUrls: ['./uitstroom.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		FilterBarComponent,
		ToggleButtonsComponent,
	],
})
export class UitstroomComponent extends BarchartTableConfig<UitstroomI, UitstroomA> {
	private readonly dataService = inject(DataService);

	readonly historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	readonly historieSubgroups = computed(() => [...this.groeperingService.huidigeGroeperingen().slice(-1), historieSchooljaarGroup]);

	override availableGroups: AttrPath[] = [
		['ds_co_brin_svh'],
		['ds_nm_klas_van'],
		['ds_nr_leerjaar_van'],
		['ds_fk_ilt_van', 'ilt_nm_niveau'],
		['ds_fk_ll', 'll_nm_niveau_svb'],
		['ds_fk_ll', 'll_nr_leerjaar_svb'],
		['ds_nm_opleiding_van'],
		['ds_fk_ilt_van', 'ilt_nm_opleiding'],
		['ds_fk_ll', 'll_nm_svb'],
		['ds_fk_ll', 'll_nm_svh'],
		['ds_fk_ll', 'll_nm_schoolsoort_svb'],
		['ds_fk_ll', 'll_des_uitschrijving'],
		['ds_fk_vs_van', 'vs_nm_vestiging'],
		['ds_fk_vs_naar', 'vs_nm_vestiging'],
		['ds_fk_ll', 'll_nm_geslacht'],
	];

	actueelFilters: FilterName[] = [...defaultDoorstroomActueelFilters, 'ds_nm_uitstroomtype_in_schooljaar', 'ds_nm_uitstroommoment_in_schooljaar'];

	historieFilters: FilterName[] = [
		...defaultDoorstroomHistorieFilters,
		'ds_nm_uitstroomtype_in_schooljaar',
		'ds_nm_uitstroommoment_in_schooljaar',
		'ds_fk_ll.ll_nm_svb',
	];

	permanentFilterExpressions = [
		new BasicFilterExpression(['ds_is_relevante_doorstroom'], 1),
		new BasicFilterExpression(['ds_nm_uitstroom_in_schooljaar'], null, '<>'),
		new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
	];

	variant = this.qp.signal('variant');

	eenheid = this.qp.signal('eenheid');

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		const xa = this.variant() === DashboardVariant.ACTUEEL ? [] : [xAggExcept(options.g!, historieSchooljaarGroup)];
		return this.dataService.getDoorstroomData({ ...options, xa, m: [DoorstroomMeasure.LEERLINGEN], r: range(0, this.getRowLevel() + 1) });
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	protected override singleAggregators = {
		ds_m_nr_weging: sumOver<'ds_m_nr_weging', UitstroomI, number>('ds_m_nr_weging'),
		ds_is_prognose: maxOverMapped<UitstroomI>((v) => Number(v.ds_is_prognose)),
		maxPctJaar: maxDeelVanJaar('ds_m_nr_weging'),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', UitstroomI>('ds_m_nr_leerlingen'),
	};

	protected override multiAggregators = [
		maxSumOfStacksIf<'max', 'ds_m_nr_weging', UitstroomA, UitstroomI>(
			'max',
			'ds_m_nr_weging',
			() => this.groeperingService.huidigeAantalGroeperingen() + (this.variant() === DashboardVariant.HISTORIE ? 1 : 0),
			() => true
		),
	];

	override createMeasureColumns(context: DashboardContext<UitstroomI, UitstroomA, UitstroomComponent>): ColumnDef<DataRow<UitstroomA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			this.createLeerlingColumn(MeasureColumn.LEERLINGEN, att('ds_m_nr_weging'), { context, format: '1.0-2' }, count_records),
			this.createLeerlingColumn(
				MeasureColumn.PERCENTAGE,
				percOfTotal('ds_m_nr_weging', 'ds_m_nr_weging'),
				{ context, dataType: 'percentage' },
				count_records
			),
		];
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	override getBarchartQty(path: Path<UitstroomA, number[]>) {
		if ((this.variant() === DashboardVariant.HISTORIE && this.eenheid()) == Eenheid.PERCENTAGE) return att0('maxPctJaar')(path);
		else return att0('ds_m_nr_weging')(path);
	}

	override createXAxis(context: DashboardContext<UitstroomI, UitstroomA, UitstroomComponent>): Axis {
		return { min: 0, max: context.dataRoot!.a.max, ticks: [] };
	}

	override createYAxis(context: DashboardContext<UitstroomI, UitstroomA, UitstroomComponent>): Axis {
		if (!context.dataRoot) return super.createYAxis(context);
		if (this.eenheid() === Eenheid.AANTAL) {
			return createYAxis([0, context.dataRoot.a.max]);
		} else {
			return createYAxis([0, context.dataRoot.a.maxPctJaar], 5, (val) => formatPercent(val, 'nl_NL'));
		}
	}

	override getDefaultSort(): Sort {
		return { active: MeasureColumn.LEERLINGEN, direction: 'desc' };
	}

	readonly partitionMeasure: Signal<PartitionMeasure<UitstroomA>> = computed(() => {
		if (this.eenheid() === Eenheid.AANTAL)
			return {
				type: 'number',
				getValue: att('ds_m_nr_weging'),
				format: '1.0-2',
			};
		else
			return {
				type: 'percentage',
				getValue: att('maxPctJaar'),
			};
	});

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override enrichTableModel(_context: DashboardContext<UitstroomI, UitstroomA, UitstroomComponent>, tableModel: TableModel<DataRow<UitstroomA>>) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	override onContextCreated(context: DashboardContext<UitstroomI, UitstroomA, UitstroomComponent>) {
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(context.dataRoot?.a.ds_is_prognose)));
	}

	override getSelectionConfig(context: DataRequestContext<UitstroomA>): SelectionConfig<UitstroomA> | undefined {
		return {
			...super.getSelectionConfig(context)!,
			getSize: att('ds_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	override makeBar(
		attrs: UitstroomI,
		path: Path<UitstroomA, number[]>,
		context: DashboardContext<UitstroomI, UitstroomA, BaseDashboardConfig<UitstroomI, UitstroomA>>
	): BarInfo {
		const bar = super.makeBar(attrs, path, context);
		return { ...bar, className: Number(attrs.ds_is_prognose) ? 'dashboard-prognose' : bar.className, description: 'Uitstroom' };
	}

	protected readonly DashboardVariant = DashboardVariant;
	protected readonly Eenheid = Eenheid;
}
